/**
 * desktopSession — shared vault-write + cookie-seed helper.
 *
 * Aug 26, 2026: the desktop login now has TWO paths that produce a
 * fresh token bundle:
 *
 *   1. **BFF inline form** (``DesktopLoginForm``) — the multi-step
 *      email/password/mfa flow runs inline on the Tauri card. When the
 *      BE inlines a ``tokens`` bundle in its login response, we drop
 *      it straight into the OS keychain (Rust ``keyring``) via this module.
 *   2. **SSO loopback exchange** (``useDesktopOAuth``) — the "Continua
 *      con SSO nel browser" fallback opens the system browser, waits
 *      for the loopback ``/callback`` bounce, and POSTs the code to
 *      ``/auth/desktop/exchange``. The response tokens land here.
 *
 * Both paths converge on ``persistDesktopSession`` so a single call
 * writes the keychain token vault + seeds the cookie in one atomic-ish
 * sequence. Any future login path (device-code, magic-link, …) SHOULD
 * come through here rather than duplicating the sequence — a diverging
 * cookie/vault write is how "logged-in-but-signed-out" bugs happen.
 *
 * This module knows NOTHING about the enterprise-DB DSN vault: that is
 * owned end-to-end by Rust (OS keychain, keyed by handle) and needs no
 * per-user binding from the login path.
 */

import {
  saveDesktopTokens,
  type DesktopTokenState,
} from "./tokenStore";
import type { DesktopTokensResponse } from "@ando/core";

/**
 * Alias exported for downstream callers that want a name that reads as
 * "the wire payload we accept" rather than the ``@ando/core`` type it
 * is under the hood. Keeping the alias here means the desktop feature
 * has ONE canonical import point for the payload shape.
 */
export type BFFLoginTokensPayload = DesktopTokensResponse;

// ── Cookie plumbing ─────────────────────────────────────────────────

const SESSION_COOKIE_NAME = "ando_session";

/**
 * Resolve the cookie ``Domain`` attribute from the runtime config
 * (``window.__ANDO_RUNTIME__``) so a single hashed bundle serves every
 * environment. Local dev returns ``""`` (host-only cookie); prod
 * returns something like ``.ando-ai.com`` so the cookie is visible to
 * both the BFF (``beta-web``) and the API (``api.ando-ai.com``).
 *
 * Mirrors the reader in ``packages/core/src/auth/session.ts`` — kept as
 * a local helper (not an import) so the desktop feature does NOT take a
 * dep on the ``session`` module's internals.
 */
function getCookieDomain(): string {
  const runtime = (globalThis as { __ANDO_RUNTIME__?: { cookieDomain?: string } })
    .__ANDO_RUNTIME__;
  return runtime?.cookieDomain || "";
}

/**
 * Compute the ``DesktopTokenState`` (in-memory shape) from a raw BE
 * ``DesktopTokensResponse``. Pure: no I/O, safe to call anywhere.
 *
 * ``expires_in`` on the wire is a seconds-from-now number; we snap it
 * to an absolute epoch-ms at receipt time so the silent-refresh
 * scheduler can compare with ``Date.now()`` without re-doing the
 * arithmetic on every tick.
 */
export function tokensToDesktopState(
  resp: DesktopTokensResponse,
): DesktopTokenState {
  return {
    access_token: resp.access_token,
    refresh_token: resp.refresh_token,
    id_token: resp.id_token,
    // Snap the relative ``expires_in`` (seconds) to an absolute epoch
    // (ms) at receipt. Reader arithmetic stays trivial forever.
    expires_at: Date.now() + resp.expires_in * 1000,
    user_info: resp.user_info,
  };
}

/**
 * Seed a JS-visible session marker cookie so any code path that keys
 * off ``document.cookie`` (legacy nav guards, session-timeout badges,
 * dev-tooling) sees "authenticated" the moment the tokens land — not
 * only after the next round-trip through ``/auth/me``.
 *
 * Best-effort by nature: WebKit inside the Tauri ``tauri://`` scheme
 * silently drops writes to ``document.cookie``. That's fine — the
 * authoritative session on desktop is the keychain token vault, and the
 * ApiClient sends the ``Authorization: Bearer <access_token>`` header
 * from ``getDesktopAccessToken`` on every call.
 *
 * The cookie value is intentionally the ``access_token`` itself so a
 * BFF that reads either ``ando_session`` cookie OR the Bearer header
 * accepts the same session identity on both paths.
 */
export function persistDesktopSessionCookie(
  tokens: DesktopTokensResponse,
): void {
  if (typeof document === "undefined") return;
  const cookieDomain = getCookieDomain();
  const domain = cookieDomain ? `; Domain=${cookieDomain}` : "";
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  // Align cookie expiry with the token TTL so a stale cookie can't
  // out-live the token it points at. Reader logic then only needs to
  // check "cookie present" — the browser retires it in lockstep.
  const expires = new Date(
    Date.now() + Math.max(tokens.expires_in, 1) * 1000,
  ).toUTCString();
  document.cookie = `${SESSION_COOKIE_NAME}=${encodeURIComponent(
    tokens.access_token,
  )}; Path=/; Expires=${expires}; SameSite=Lax${secure}${domain}`;
}

/**
 * Single-shot "session freshly issued" plumbing:
 *   1. Compute the in-memory ``DesktopTokenState``.
 *   2. Write it through to the OS keychain (``saveDesktopTokens``).
 *   3. Seed the JS-visible ``ando_session`` cookie.
 *
 * Returns the newly persisted state so the caller can set React
 * state (``setTokens(next)``) synchronously without an extra vault
 * read round-trip.
 *
 * Vault write comes BEFORE the cookie seed: a crash between the two
 * leaves the vault as source of truth and the cookie stale — the
 * cheaper failure mode (next boot re-hydrates fine, worst case the
 * user sees a one-frame "signed out" flash before the vault load
 * resolves). The reverse order would risk a stale cookie pointing at
 * no vault entry — the ApiClient would send the ex-token and the BE
 * would 401, forcing a full re-login.
 */
export async function persistDesktopSession(
  tokens: DesktopTokensResponse,
): Promise<DesktopTokenState> {
  const state = tokensToDesktopState(tokens);
  await saveDesktopTokens(state);
  persistDesktopSessionCookie(tokens);
  return state;
}
